import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { staffApi, customerApi, STAFF_TOKEN_KEY, CUSTOMER_TOKEN_KEY } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [staff, setStaff] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  // Populated by the axios interceptor when a request is 401-ed with
  // `code:SESSION_INVALIDATED`. Consumed by the KickedOutDialog to
  // pop the "logged in on another device" modal.
  const [kickedOut, setKickedOut] = useState(null); // { kind: "staff" | "customer" }

  const bootstrap = useCallback(async () => {
    setLoading(true);
    const staffToken = localStorage.getItem(STAFF_TOKEN_KEY);
    const customerToken = localStorage.getItem(CUSTOMER_TOKEN_KEY);
    if (staffToken) {
      try {
        const r = await staffApi.get("/auth/me");
        if (r.data?.kind === "staff") setStaff(r.data.user);
      } catch (_) {
        localStorage.removeItem(STAFF_TOKEN_KEY);
      }
    }
    if (customerToken) {
      try {
        const r = await customerApi.get("/auth/me");
        if (r.data?.kind === "customer") setCustomer(r.data.user);
      } catch (_) {
        localStorage.removeItem(CUSTOMER_TOKEN_KEY);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  // Global listener for single-session kick-out events. The axios
  // interceptor drops the token before firing so we only need to
  // clear the in-memory user + surface the modal here.
  useEffect(() => {
    const onKick = (e) => {
      const kind = e?.detail?.kind;
      if (kind === "staff") setStaff(null);
      else if (kind === "customer") setCustomer(null);
      setKickedOut({ kind: kind || "staff" });
    };
    window.addEventListener("cocoon-session-invalidated", onKick);
    return () => window.removeEventListener("cocoon-session-invalidated", onKick);
  }, []);

  const dismissKickedOut = () => setKickedOut(null);

  // Jun 2026 — employees sign in with a one-time code emailed to their
  // work address (the username/password endpoint no longer exists).
  const requestStaffOtp = async (email) => {
    const r = await staffApi.post("/auth/staff/request-otp", { email });
    return r.data; // { pending_id, email, delivered, dev_otp? }
  };
  const resendStaffOtp = async (pending_id) => {
    const r = await staffApi.post("/auth/staff/resend-otp", { pending_id });
    return r.data;
  };
  const verifyStaffOtp = async (pending_id, code) => {
    const r = await staffApi.post("/auth/staff/verify-otp", { pending_id, code });
    localStorage.setItem(STAFF_TOKEN_KEY, r.data.token);
    setStaff(r.data.user);
    return { user: r.data.user, previousSessionReplaced: !!r.data.previous_session_replaced };
  };

  const setCustomerFromToken = async (user) => {
    setCustomer(user);
  };

  // Iter-358 · Passwordless OTP login (Guest Portal only). The three
  // helpers below map 1:1 onto the backend endpoints and are consumed
  // by `pages/portal/CustomerLogin.jsx`.
  const checkCustomerEmail = async (email) => {
    const r = await customerApi.post("/auth/customer/login-check-email", { email });
    return r.data; // { email, exists }
  };
  const requestCustomerLoginOtp = async (email) => {
    const r = await customerApi.post("/auth/customer/login-request-otp", { email });
    return r.data; // { pending_id, email, delivered, dev_otp? }
  };
  const verifyCustomerLoginOtp = async (pending_id, code) => {
    const r = await customerApi.post("/auth/customer/login-verify-otp", { pending_id, code });
    localStorage.setItem(CUSTOMER_TOKEN_KEY, r.data.token);
    setCustomer(r.data.user);
    return { user: r.data.user, previousSessionReplaced: !!r.data.previous_session_replaced };
  };

  const logoutStaff = () => {
    localStorage.removeItem(STAFF_TOKEN_KEY);
    setStaff(null);
  };
  const refreshStaff = async () => {
    const staffToken = localStorage.getItem(STAFF_TOKEN_KEY);
    if (!staffToken) return null;
    try {
      const r = await staffApi.get("/auth/me");
      if (r.data?.kind === "staff") { setStaff(r.data.user); return r.data.user; }
    } catch (_) { /* keep existing staff state */ }
    return null;
  };
  const logoutCustomer = () => {
    localStorage.removeItem(CUSTOMER_TOKEN_KEY);
    setCustomer(null);
  };

  return (
    <AuthContext.Provider value={{ staff, customer, loading, requestStaffOtp, resendStaffOtp, verifyStaffOtp, setCustomerFromToken, checkCustomerEmail, requestCustomerLoginOtp, verifyCustomerLoginOtp, logoutStaff, refreshStaff, logoutCustomer, kickedOut, dismissKickedOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
